<script setup lang="ts">
import { SancareOcticon } from '@sancare/typescript-commons'
import { useStore } from 'vuex'

import { AutomationTypeEnum, ConditionOperatorEnum } from '@/store/modules/automation/types'
import { ConditionGroup, CriterionCondition } from '@/store/modules/searches/types'
import { ModeEnum, PmsiTypeEnum } from '@/store/modules/settings/types'

import CriteriaDisplayer from '../filters/CriteriaDisplayer.vue'
import FilterBuilder from '../filters/FilterBuilder.vue'

export interface AutomationCriteriaGroupProps {
  internalOperator: ConditionOperatorEnum
  group: ConditionGroup
  groupIsGlobal: boolean
  automationType: AutomationTypeEnum
}

const store = useStore()

const props = withDefaults(
  defineProps<AutomationCriteriaGroupProps>(), {
    automationType: AutomationTypeEnum.AI,
    groupIsGlobal: false,
    internalOperator: ConditionOperatorEnum.OR
  }
)

const emit = defineEmits<{
  (e: 'clear-message'): void
  (e: 'delete-criteria-group', id: string | number): void
}>()

function addCriteria({ type, value }: { type: string, value: string }) {
  if (['presentMedicalAct', 'absentMedicalAct'].indexOf(type) > -1) {
    value = value.toUpperCase()
  }

  emit('clear-message')

  if (props.groupIsGlobal) {
    store.commit('addGlobalCriteriaToList', { type, value })
  } else {
    store.commit('addCriteriaToList', {
      criteriaGroupId: props.group.id,
      type,
      value
    })
  }
}

function removeCriteria({ criterion }: { criterion: CriterionCondition }) {
  emit('clear-message')

  if (props.groupIsGlobal) {
    store.commit('removeGlobalCriteriaFromList', { criterion })
  } else {
    store.commit('removeCriteriaFromList', {
      criteriaGroupId: props.group.id,
      criterion
    })
  }
}

function deleteCriteriaGroup() {
  if (! props.groupIsGlobal) {
    emit('clear-message')
    emit('delete-criteria-group', props.group.id)
  }
}

</script>

<template>
  <div>
    <div
      :class="{
        'card': true,
        ...(groupIsGlobal ? {'card-secondary':true} : {'card-primary':true})
      }"
    >
      <div class="card-header">
        <div class="row">
          <div class="col">
            Liste de critères{{ groupIsGlobal ? ' globaux' : '' }} :
          </div>
          <div
            v-if="!groupIsGlobal"
            class="col-auto delete-criteria-group"
            @click="() => deleteCriteriaGroup(group.id)"
          >
            <sancare-octicon
              name="x"
              :width="17"
              :height="20"
            />
          </div>
        </div>
      </div>
      <div
        v-if="(group.criteriaList && group.criteriaList.length > 0)"
        class="card-body"
      >
        <criteria-displayer
          :can-remove-criteria="true"
          :internal-operator="internalOperator"
          :criteria-group="group"
          :display-grouped-criteria="groupIsGlobal"
          :is-global="groupIsGlobal"
          :mode="ModeEnum.AUTOMATION"
          @remove-criteria="removeCriteria"
        />
      </div>
      <div class="card-footer">
        <filter-builder
          :mode="ModeEnum.AUTOMATION"
          :automation-type="automationType"
          :pmsi-type="PmsiTypeEnum.MCO"
          :is-global="groupIsGlobal"
          @add-criteria="addCriteria"
        />
      </div>
    </div>
  </div>
</template>
